// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Building2, Plus, Search, Star } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Select, Pagination, DataTable, Skeleton, Input } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import type { Column } from '@/components/ui/DataTable'
import { useVendors } from '@/hooks/useCmms'
import { useListSort } from '@/hooks/useListSort'
import type { Vendor } from '@/types'

export function VendorListPage() {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [activeFilter, setActiveFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const filters = {
    active: activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined,
    q: searchQuery || undefined,
  }

  const { data, isLoading, isFetching, error } = useVendors(page, { ...filters, ...sortParams })
  const vendors = data?.data || []

  const activeOptions = [
    { value: '', label: tc('labels.all') },
    { value: 'active', label: tc('status.active') },
    { value: 'inactive', label: tc('status.inactive') },
  ]

  const columns: Column<Vendor>[] = [
    { key: 'name', header: t('labels.name'), sortable: true, render: (v) => <span className="font-medium">{v.name}</span> },
    { key: 'contact_name', header: t('labels.contact_name'), render: (v) => v.contact_name || '-', hideBelow: 'md' },
    { key: 'phone', header: t('labels.phone'), render: (v) => v.phone || '-' },
    { key: 'email', header: t('labels.email'), render: (v) => v.email || '-', hideBelow: 'md' },
    {
      key: 'specialties',
      header: t('labels.specialties'),
      render: (v) =>
        v.specialties && v.specialties.length > 0
          ? v.specialties.join(', ')
          : '-',
      hideBelow: 'lg',
    },
    {
      key: 'avg_score',
      header: t('labels.avg_score'),
      sortable: true,
      render: (v) =>
        v.avg_score != null ? (
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${i < Math.round(v.avg_score!) ? 'text-warning fill-warning' : 'text-base-300'}`}
              />
            ))}
          </div>
        ) : '-',
      hideBelow: 'lg',
    },
    {
      key: 'active',
      header: t('labels.active'),
      render: (v) =>
        v.active ? (
          <Badge variant="success">{tc('status.active')}</Badge>
        ) : (
          <Badge variant="ghost">{tc('status.inactive')}</Badge>
        ),
    },
  ]

  // EP-97: mobile sort options mirror the allow-listed sortable columns.
  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'created_at', label: tc('sort.by_newest') },
    { value: 'name', label: t('labels.name') },
    { value: 'avg_score', label: t('labels.avg_score') },
  ]

  const activeFilterCount = [activeFilter].filter(Boolean).length

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('vendors.title') }]} />

      <PageHeader
        title={t('vendors.title')}
        description={t('vendors.description')}
        isRefreshing={isFetching && !isLoading}
        actions={
          <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/vendors/new')}>
            {t('vendors.new')}
          </Button>
        }
      />

      <Card>
        {/* Search — always visible */}
        <div className="w-full min-w-0">
          <Input
            label={tc('actions.search')}
            leftIcon={<Search className="w-4 h-4" />}
            placeholder={t('filters.search_vendors')}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
          />
        </div>

        {/* Mobile-only toggle */}
        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
          aria-expanded={mobileFiltersOpen}
          aria-controls="vendor-filters-body"
          onClick={() => setMobileFiltersOpen((v) => !v)}
        >
          <span>
            {tc('filters.toggle')}
            {activeFilterCount > 0 && (
              <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
            )}
          </span>
          <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>

        <div
          id="vendor-filters-body"
          className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
        >
          {/* EP-97: mobile sort control — desktop sorts via clickable table headers. */}
          <div className="w-full sm:hidden">
            <MobileSortControl
              options={sortOptions}
              value={sort}
              order={order}
              onChange={handleSort}
              onToggleOrder={handleToggleOrder}
            />
          </div>
          <Select
            label={tc('labels.status')}
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value); setPage(1) }}
            options={activeOptions}
            className="w-full sm:w-44 min-w-0"
          />
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={t('messages.error_loading')}
            description={t('messages.error_description')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : !vendors.length ? (
        <Card>
          <EmptyState
            icon={<Building2 className="w-12 h-12" />}
            title={t('empty.vendors')}
            action={
              <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/vendors/new')}>
                {t('vendors.new')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={vendors}
            keyExtractor={(v) => v.id}
            onRowClick={(v) => navigate(`/vendors/${v.id}`)}
            sortBy={sort}
            sortOrder={order}
            onSort={handleSort}
          />
        </Card>
      )}

      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
